// VENPOS SERVICE 2 - CONFIGURED FOR FREE ORDER BOOKING JOURNEY
// Location ID: 978
// Page ID: 278ef0c8-a65b-4e31-9726-305ae06a5a14

const VENPOS_API_BASE = 'https://online1.venpos.net/VenposJourneyAPI';
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

// Configuration for free order booking journey
const VENPOS_CONFIG = {
  locationId: 978,
  pageId: '278ef0c8-a65b-4e31-9726-305ae06a5a14',
  defaultLocationId: 978,
  defaultPageId: '278ef0c8-a65b-4e31-9726-305ae06a5a14'
};

/*
 * VENPOS API INTEGRATION - VERSION 2
 *
 * This service is specifically configured for free order bookings using:
 * - Location ID: 978
 * - Page ID: 278ef0c8-a65b-4e31-9726-305ae06a5a14
 *
 * Customer information is handled through the basket and payment flow:
 * - Customer details are passed to Basket/Load via customerId and customer object
 * - Customer validation happens during Payment/Initialize
 * - No separate CustomerDetails endpoints exist
 *
 * Working endpoints:
 * - Basket/Create (POST)
 * - Basket/Load (POST)
 * - Payment/Initialize (POST)
 * - Payment/Complete (POST)
 */

// Standard titles and countries for forms (static data is appropriate here)
const FORM_DATA = {
  titles: [
    { id: 1, name: 'Mr' }, { id: 2, name: 'Mrs' }, { id: 3, name: 'Miss' },
    { id: 4, name: 'Ms' }, { id: 5, name: 'Dr' }, { id: 6, name: 'Prof' },
    { id: 7, name: 'Rev' }, { id: 8, name: 'Sir' }, { id: 9, name: 'Lady' },
    { id: 10, name: 'Lord' }
  ],
  countries: [
    { code: 'UK', name: 'United Kingdom' }, { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' }, { code: 'AU', name: 'Australia' },
    { code: 'IE', name: 'Ireland' }, { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' }, { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' }, { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' }, { code: 'CH', name: 'Switzerland' },
    { code: 'AT', name: 'Austria' }, { code: 'DK', name: 'Denmark' },
    { code: 'SE', name: 'Sweden' }, { code: 'NO', name: 'Norway' },
    { code: 'FI', name: 'Finland' }, { code: 'PL', name: 'Poland' },
    { code: 'CZ', name: 'Czech Republic' }, { code: 'HU', name: 'Hungary' },
    { code: 'PT', name: 'Portugal' }, { code: 'GR', name: 'Greece' },
    { code: 'JP', name: 'Japan' }, { code: 'NZ', name: 'New Zealand' },
    { code: 'ZA', name: 'South Africa' }
  ]
};

// Helper function to generate GUIDs for customerId and orderId
const generateGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Main VenposService class
class VenposService2 {
  constructor() {
    this.apiBase = VENPOS_API_BASE;
    this.config = VENPOS_CONFIG;
  }

  // Helper method to make API calls with error handling
  async apiCall(endpoint, options = {}) {
    const url = `${this.apiBase}${endpoint}`;

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    };

    try {

      if (options.body) {

      }

      const response = await fetch(url, config);

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      return {
        isError: false,
        data: data,
        status: response.status
      };
    } catch (error) {

      return {
        isError: true,
        error: error.message,
        status: 0
      };
    }
  }

  // Get web sale items for the configured page
  async getWebSaleItems(locationId = this.config.defaultLocationId, webPageId = this.config.defaultPageId, selectedDate = null) {
    const response = await fetch(
      `${VENPOS_API_BASE}/WebSaleItem/Get?` +
      `locationId=${locationId}&` +
      `id=${webPageId}&` +
      `pageType=WebPage&` +
      (selectedDate ? `selectedDate=${selectedDate}&` : '') +
      `mainWebPageId=${webPageId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch web sale items: ${response.status}`);
    }

    const data = await response.json();
    return {
      isError: false,
      data: data
    };
  }

  // Get available dates for selected items
  async getAvailableDates(locationId = this.config.defaultLocationId, pageId = this.config.defaultPageId, options = {}, selectedItems = []) {
    const today = new Date();
    const endDate = new Date();
    endDate.setDate(1); // Set to 1st to avoid month overflow issues
    endDate.setMonth(endDate.getMonth() + 12);

    const {
      pageType = 'WebPage',
      selectedDate = today.toISOString().split('T')[0],
      dateSelectorType = 'OnScreenCalendar'
    } = options;

    const requestBody = {
      locationId,
      pageId,
      pageType,
      selectedDate,
      dateSelectorType,
      items: selectedItems.map(item => ({
        pageType: item.pageType || 'WebPage',
        pageId: item.pageId || pageId,
        webSaleItemId: item.webSaleItemId,
        quantity: item.quantity || 1,
        webSaleItemTypeID: item.webSaleItemTypeID || 2,
        selectedDate: item.selectedDate || selectedDate,
        name: item.name || 'Ticket'
      }))
    };

    return this.apiCall('/WebSaleItem/GetCalendar', {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
  }

  // Get time slots for selected date and items
  async getTimeSlots(locationId = this.config.defaultLocationId, pageId = this.config.defaultPageId, selectedDate, selectedItems = []) {
    try {
      const selectedDateKey = new Date(selectedDate).toISOString().split('T')[0];

      // Use the Event/GetTimes endpoint as per API documentation
      const url = new URL(`${VENPOS_API_BASE}/Event/GetTimes`);
      url.searchParams.append('locationId', locationId);
      url.searchParams.append('mainWebPageId', pageId);
      url.searchParams.append('id', pageId);
      url.searchParams.append('pageType', 'WebPage');
      url.searchParams.append('date', selectedDateKey);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: selectedItems.map(item => ({
            pageType: item.pageType || 'WebPage',
            pageId: item.pageId || pageId,
            webSaleItemId: item.webSaleItemId,
            quantity: item.quantity || 1,
            webSaleItemTypeID: item.webSaleItemTypeID || 2,
            selectedDate: item.selectedDate || selectedDateKey + 'T10:00:00',
            name: item.name || 'Ticket'
          }))
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch time slots: ${response.status}`);
      }

      const data = await response.json();

      return {
        isError: false,
        data: data
      };
    } catch (error) {

      return {
        isError: true,
        error: error.message
      };
    }
  }

  // Add event capacity usages (reserves capacity for selected items)
  async addEventCapacityUsages(locationId = this.config.defaultLocationId, pageId = this.config.defaultPageId, itemsPayload, orderId = null, discounts = []) {
    try {
      const url = new URL(`${VENPOS_API_BASE}/Event/AddEventCapacityUsages`);
      url.searchParams.append('locationId', locationId);

      // Generate a real orderId if not provided (working example always has one)
      const actualOrderId = orderId || generateGuid();

      // Ensure each item has required fields for capacity reservation
      const enhancedItems = itemsPayload.map(item => ({
        ...item,
        pageType: item.pageType || 'WebPage',
        pageId: item.pageId || pageId,
        webSaleItemId: item.webSaleItemId,
        quantity: item.quantity || 1,
        webSaleItemTypeID: item.webSaleItemTypeID || 2,
        selectedDate: item.selectedDate || new Date().toISOString(),
        reservationExpiryDateTime: item.reservationExpiryDateTime || new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        name: item.name || 'Ticket',
        giftAidEligible: item.giftAidEligible || false,
        isSysGift: item.isSysGift || false,
        isSysMemberCard: item.isSysMemberCard || false,
        eventId: item.eventId || null,
        eventCapacityUsageId: item.eventCapacityUsageId || null
      }));

      const requestBody = {
        orderId: actualOrderId,
        items: enhancedItems,
        discounts: discounts || []
      };

      return this.apiCall('/Event/AddEventCapacityUsages', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
    } catch (error) {

      return {
        isError: true,
        error: error.message
      };
    }
  }

  // Validate basket times
  async validateBasketTimes(locationId = this.config.defaultLocationId, itemsPayload) {
    try {
      const url = new URL(`${VENPOS_API_BASE}/Basket/ValidateTimes`);
      url.searchParams.append('locationId', locationId);

      const requestBody = {
        items: itemsPayload.map(item => ({
          ...item,
          pageType: item.pageType || 'WebPage',
          pageId: item.pageId || this.config.defaultPageId,
          webSaleItemId: item.webSaleItemId,
          quantity: item.quantity || 1,
          webSaleItemTypeID: item.webSaleItemTypeID || 2,
          selectedDate: item.selectedDate || new Date().toISOString(),
          name: item.name || 'Ticket',
          giftAidEligible: item.giftAidEligible || false,
          isSysGift: item.isSysGift || false,
          isSysMemberCard: item.isSysMemberCard || false,
          eventId: item.eventId || null,
          eventCapacityUsageId: item.eventCapacityUsageId || null
        }))
      };

      return this.apiCall('/Basket/ValidateTimes', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
    } catch (error) {

      return {
        isError: true,
        error: error.message
      };
    }
  }

  // Create basket with validation
  async createBasketWithValidation(locationId = this.config.defaultLocationId, basketItems, customerDetails = null) {
    try {

      // Use Basket/Load directly (which creates and loads in one call)
      const loadPayload = {
        locationId,
        pageId: this.config.defaultPageId,
        pageType: 'WebPage',
        items: basketItems.map(item => ({
          pageType: item.pageType || 'WebPage',
          pageId: item.pageId || this.config.defaultPageId,
          webSaleItemId: item.webSaleItemId,
          webSaleItemTypeID: item.webSaleItemTypeID || 2,
          quantity: item.quantity || 1,
          selectedDate: item.selectedDate || new Date().toISOString(),
          name: item.name || 'Ticket',
          giftAidEligible: item.giftAidEligible || false,
          isSysGift: item.isSysGift || false,
          isSysMemberCard: item.isSysMemberCard || false,
          eventId: item.eventId || null,
          eventCapacityUsageId: item.eventCapacityUsageId || null
        })),
        giftAidSelected: false,
        discounts: [],
        discountsFromMembership: [],
        // Include customer details if available
        ...(customerDetails && {
          customer: {
            firstName: customerDetails.firstName,
            lastName: customerDetails.lastName,
            email: customerDetails.email,
            mobile: customerDetails.phone || customerDetails.mobile || '',
            addressFirstLine: customerDetails.address?.line1 || '',
            addressSecondLine: customerDetails.address?.line2 || '',
            cityTown: customerDetails.address?.city || '',
            postCode: customerDetails.address?.postcode || '',
            selectedCountry: customerDetails.address?.country || 'UK'
          }
        })
      };

      return this.apiCall('/Basket/Load', {
        method: 'POST',
        body: JSON.stringify(loadPayload)
      });
    } catch (error) {

      return {
        isError: true,
        error: error.message
      };
    }
  }

  // Load basket (for refreshing existing basket)
  async loadBasket(locationId = this.config.defaultLocationId, loadPayload, familySaverCode = null) {
    try {

      // Apply family saver code if provided
      if (familySaverCode) {
        loadPayload.discounts = loadPayload.discounts || [];
        loadPayload.discounts.push(familySaverCode);
      }

      return this.apiCall('/Basket/Load', {
        method: 'POST',
        body: JSON.stringify(loadPayload)
      });
    } catch (error) {

      return {
        isError: true,
        error: error.message
      };
    }
  }

  // Apply voucher code to basket
  async applyVoucherCode(locationId = this.config.defaultLocationId, basketId, voucherCode) {
    try {
      const url = new URL(`${VENPOS_API_BASE}/Basket/ApplyVoucher`);
      url.searchParams.append('locationId', locationId);
      url.searchParams.append('basketId', basketId);

      const requestBody = {
        voucherCode: voucherCode
      };

      return this.apiCall('/Basket/ApplyVoucher', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
    } catch (error) {

      return {
        isError: true,
        error: error.message
      };
    }
  }

  // Get upsells for selected items
  async getUpsells(locationId = this.config.defaultLocationId, pageId = this.config.defaultPageId, eventType, dateSelectorType, items) {
    try {

      const requestBody = {
        locationId,
        pageId,
        pageType: 'WebPage',
        eventType,
        dateSelectorType,
        items: items.map(item => ({
          pageType: item.pageType || 'WebPage',
          pageId: item.pageId || pageId,
          webSaleItemId: item.webSaleItemId,
          quantity: item.quantity || 1,
          webSaleItemTypeID: item.webSaleItemTypeID || 2,
          selectedDate: item.selectedDate || new Date().toISOString(),
          name: item.name || 'Ticket',
          giftAidEligible: item.giftAidEligible || false,
          isSysGift: item.isSysGift || false,
          isSysMemberCard: item.isSysMemberCard || false,
          eventId: item.eventId || null,
          eventCapacityUsageId: item.eventCapacityUsageId || null
        }))
      };

      return this.apiCall('/WebSaleItem/GetUpsells', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
    } catch (error) {

      return {
        isError: true,
        error: error.message
      };
    }
  }

  // Add items to existing basket
  async addToBasket(basketId, locationId = this.config.defaultLocationId, items) {
    try {

      const requestBody = {
        basketId,
        locationId,
        items: items.map(item => ({
          pageType: item.pageType || 'WebPage',
          pageId: item.pageId || this.config.defaultPageId,
          webSaleItemId: item.webSaleItemId,
          quantity: item.quantity || 1,
          webSaleItemTypeID: item.webSaleItemTypeID || 2,
          selectedDate: item.selectedDate || new Date().toISOString(),
          name: item.name || 'Ticket',
          giftAidEligible: item.giftAidEligible || false,
          isSysGift: item.isSysGift || false,
          isSysMemberCard: item.isSysMemberCard || false,
          eventId: item.eventId || null,
          eventCapacityUsageId: item.eventCapacityUsageId || null
        }))
      };

      return this.apiCall('/Basket/AddToBasket', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
    } catch (error) {

      return {
        isError: true,
        error: error.message
      };
    }
  }

  // Complete payment (for free orders, this just confirms the booking)
  async completePayment(locationId = this.config.defaultLocationId, basketId, paymentToken, paymentResult) {
    try {

      // For free orders, we don't need actual payment processing
      // Just confirm the booking is complete
      const requestBody = {
        basketId,
        locationId,
        paymentToken: paymentToken || 'FREE_ORDER_TOKEN',
        paymentResult: {
          ...paymentResult,
          method: 'free_order',
          completed: true,
          amount: 0
        }
      };

      return this.apiCall('/Payment/Complete', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
    } catch (error) {

      return {
        isError: true,
        error: error.message
      };
    }
  }

  // Confirm booking (final step for free orders)
  async confirmBooking(basketId, locationId = this.config.defaultLocationId, bookingData) {
    try {

      const requestBody = {
        basketId,
        locationId,
        selectedDate: bookingData.selectedDate,
        selectedTimeSlot: bookingData.selectedTimeSlot,
        selectedTickets: bookingData.selectedTickets,
        pageId: this.config.defaultPageId,
        totalAmount: bookingData.totalAmount || 0,
        paymentToken: bookingData.paymentToken || 'FREE_ORDER_TOKEN',
        transactionId: bookingData.transactionId || 'FREE_ORDER',
        customerDetails: bookingData.customerDetails,
        paymentDetails: bookingData.paymentDetails || { method: 'free_order' },
        discounts: bookingData.discounts || [],
        familySaverCode: bookingData.familySaverCode || null
      };

      return this.apiCall('/Basket/Confirm', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
    } catch (error) {

      return {
        isError: true,
        error: error.message
      };
    }
  }

  // Get customer titles
  getTitles() {
    return FORM_DATA.titles;
  }

  // Get countries
  getCountries() {
    return FORM_DATA.countries;
  }
}

// Create and export a singleton instance
export const venposService2 = new VenposService2();
